import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppSelector } from '@app/hooks';
import { useAgentPermission } from '@entities/user';
import type {
  ActivityTiming,
  AuditEvent,
  KspAction,
  KspWorkspace,
} from '@entities/ksp/types';
import { useKspCollections } from '@entities/ksp/useCollections';
import {
  useGetKspWorkspaceQuery,
  useMutateKspMutation,
  useImportKspMutation,
  useUploadKspOriginalMutation,
  useGetKspImportQuery,
  useLazyGetKspCollectionQuery,
  useGetKspFieldFrontsQuery,
} from '@shared/api/endpoints/ksp';
import {
  useCreateRequestMutation,
  useGetMtoRunQuery,
  useListMtoRunsQuery,
} from '@shared/api';
import { useActiveProjectContext } from '@shared/lib/activeProjectContext';
import { Button } from '@shared/ui/Button';
import { Card, Notice, Stack } from './styles';
import { RdDatesTab } from '../rdDates/index';
import { CollectionContext } from './types';
import { WorkspaceHeader } from './components/WorkspaceHeader';
import { ActionModal } from './components/ActionModal';
import { ExportStatus } from './components/ExportStatus';
import { InitialSetup } from './components/InitialSetup';
import { useWorkspaceOptions } from './hooks/useWorkspaceOptions';
import { useWorkspaceActions } from './hooks/useWorkspaceActions';
import { OverviewTab } from './tabs/OverviewTab';
import { SourcesTab } from './tabs/SourcesTab';
import { ScheduleTab } from './tabs/ScheduleTab';
import { PeriodsTab } from './tabs/PeriodsTab';
import { ActualTab } from './tabs/ActualTab';
import { PlanFactTab } from './tabs/PlanFactTab';
import { MilestonesTab } from './tabs/MilestonesTab';
import { RecoveryTab } from './tabs/RecoveryTab';
import { DocumentsTab } from './tabs/DocumentsTab';
import { HistoryTab } from './tabs/HistoryTab';

export function WorkspaceView({ projectId }: { projectId: string }) {
  const { setActiveProjectId } = useActiveProjectContext();
  useEffect(() => {
    if (projectId) {
      setActiveProjectId(projectId);
    }
  }, [projectId, setActiveProjectId]);

  const [createFunctionRequest, { isLoading: creatingReq }] = useCreateRequestMutation();
  const mtoRunsQ = useListMtoRunsQuery({ limit: 50 });
  const mtoRuns = mtoRunsQ.data?.items ?? [];
  const latestMtoRunId = mtoRuns[0]?.id ?? '';
  const latestMtoRunQ = useGetMtoRunQuery(latestMtoRunId, { skip: !latestMtoRunId });

  const fieldSitesQ = useGetKspFieldFrontsQuery(projectId);
  const fieldSites = (fieldSitesQ.currentData?.sites ?? []).filter(
    (s) => s.project_id === projectId,
  );

  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? 'overview';
  const { can_write } = useAgentPermission('ksp');

  const {
    data: source,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetKspWorkspaceQuery(
    {
      projectId,
      versionId: params.get('version') ?? undefined,
      snapshotId: params.get('snapshot') ?? undefined,
      period: params.get('period') ?? undefined,
    },
    { pollingInterval: 15000 },
  );

  const {
    workspace: data,
    loadMore,
    loading: collectionLoading,
  } = useKspCollections(source, {
    projectId,
    versionId: params.get('version') ?? undefined,
    snapshotId: params.get('snapshot') ?? undefined,
  });

  const [mutate, mutation] = useMutateKspMutation();
  const [upload, uploading] = useImportKspMutation();
  const [uploadOriginal, uploadingOriginal] = useUploadKspOriginalMutation();
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const [exportId, setExportId] = useState('');
  const [preparedMessages, setPreparedMessages] = useState<Record<string, string>>({});
  const [fetchAuditPage, fetchingAuditPage] = useLazyGetKspCollectionQuery();
  const [auditDetails, setAuditDetails] = useState<Record<string, AuditEvent>>({});
  const [auditPending, setAuditPending] = useState('');
  const [proposal, setProposal] = useState<{
    id: string;
    action: string;
    text: string;
    refs: string[];
  } | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [importKind, setImportKind] = useState('schedule');
  const [dateFormat, setDateFormat] = useState('');
  const [separator, setSeparator] = useState(';');
  const [detailedImportId, setDetailedImportId] = useState('');
  const {
    data: detailedImport,
    isFetching: loadingImport,
    error: importError,
  } = useGetKspImportQuery({ projectId, id: detailedImportId }, { skip: !detailedImportId });
  const [dailyItems, setDailyItems] = useState<
    { activity_id: string; date: string; quantity: string; unit: string; responsible_id: string }[]
  >([]);
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const token = useAppSelector((state) => state.auth.accessToken);

  const {
    mtoRunOptions,
    mtoRowOptions,
    fieldSiteOptions,
    workOptions,
    calendarOptions,
    wbsOptions,
    letterOptions,
    evidenceOptions,
    fileOptions,
    exportObjects,
  } = useWorkspaceOptions({
    mtoRuns,
    latestMtoRunData: latestMtoRunQ.data,
    fieldSites,
    data,
  });

  const versionId = data?.selected_version_id;

  const {
    form,
    setForm,
    fail,
    execute,
    open,
    registerDispatchEvidence,
    activityForm,
    createDocument,
    exportForm,
  } = useWorkspaceActions({
    projectId,
    versionId,
    data,
    params,
    mutate,
    setMessage,
    setErrors,
    setExportId,
    wbsOptions,
    calendarOptions,
    fileOptions,
    workOptions,
    exportObjects,
  });

  const permission = (action: KspAction = 'edit') => {
    const aliases: Partial<Record<KspAction, string>> = {
      manage: 'manage_grants',
      publish: 'approve',
      'period.release': 'release_period',
      'actual.accept': 'accept_actual',
      'actual.correct': 'accept_actual',
      'letter.send': 'edit',
      'evidence.verify': 'verify_dispatch',
      'evidence.revoke': 'close_alert',
      'alert.close': 'close_alert',
    };
    return (
      can_write &&
      !data?.history_read_only &&
      !!data?.grants?.some((grant) => grant === action || grant === aliases[action])
    );
  };

  const version = data?.versions?.find((v) => v.id === data.selected_version_id);
  const editable =
    permission() && (!version || ['draft', 'internal_review'].includes(version.approval));
  const timezone = data?.settings?.timezone ?? 'Europe/Moscow';

  const loadAuditDetails = async (event: AuditEvent) => {
    const offset = data?.audit_events.findIndex((row) => row.id === event.id) ?? -1;
    if (offset < 0) return;
    const identity = `${projectId}:${event.id}`;
    setAuditPending(identity);
    try {
      const page = await fetchAuditPage(
        { projectId, collection: 'audit_events', offset, limit: 1 },
        false,
      ).unwrap();
      const full = page.rows[0];
      if (page.offset !== offset || full?.id !== event.id)
        throw new Error('Запись истории изменилась. Обновите данные и повторите загрузку.');
      setAuditDetails((previous) => ({
        ...previous,
        [identity]: {
          id: event.id,
          action: full.action as string,
          object_id: full.object_id as string | null,
          actor: full.actor as string | null,
          at: full.at as string,
          version: full.version as number | undefined,
          reason: typeof full.reason === 'string' ? full.reason : '',
          source_refs: Array.isArray(full.source_refs)
            ? (full.source_refs as AuditEvent['source_refs'])
            : [],
          payload_deferred: false,
        },
      }));
    } catch (error) {
      fail(error);
    } finally {
      setAuditPending((previous) => (previous === identity ? '' : previous));
    }
  };

  if (isLoading) return <Notice>Загружаются данные проекта КСП…</Notice>;
  if (!data)
    return (
      <Notice $error>
        Проект недоступен или данные не загружены.
        <Button onClick={() => refetch()}>Повторить</Button>
      </Notice>
    );

  if (data.initialized === false)
    return (
      <InitialSetup
        projectId={projectId}
        canWrite={can_write}
        historyReadOnly={data.history_read_only}
        busy={mutation.isLoading}
        errors={errors}
        refetch={() => refetch()}
        execute={execute}
      />
    );

  const d: KspWorkspace = {
    ...data,
    imports: data.imports.map((preview) =>
      preview.id === detailedImport?.id
        ? {
            ...preview,
            ...detailedImport,
            rows_truncated: false,
            issues_truncated: false,
            row_count: detailedImport.rows.length,
            issue_count: detailedImport.issues.length,
          }
        : preview,
    ),
  };

  const activityTimings: ActivityTiming[] = d.activities.flatMap((activity) =>
    activity.forecast_start && activity.forecast_finish
      ? [
          {
            id: activity.id,
            early_start: activity.forecast_start,
            early_finish: activity.forecast_finish,
            total_float_minutes: activity.total_float_minutes,
            critical: activity.critical ?? false,
          },
        ]
      : [],
  );

  const timestamps: ActivityTiming[] = activityTimings.length
    ? activityTimings
    : (d.summary?.calculation?.timings ??
      (d.history_read_only
        ? []
        : (d.calculations.find((r) => ['succeeded', 'completed'].includes(r.status))?.snapshot
            ?.calculation?.timings ??
          d.calculations.find((r) => ['succeeded', 'completed'].includes(r.status))?.output
            ?.timings ??
          [])));

  return (
    <CollectionContext.Provider
      value={{
        workspace: d,
        loading: collectionLoading,
        load: (collection) => {
          void loadMore(collection).catch(fail);
        },
      }}
    >
      <Stack>
        <WorkspaceHeader
          workspace={d}
          timezone={timezone}
          version={version}
          isFetching={isFetching}
          canWrite={can_write}
          tab={tab}
          params={params}
          message={message}
          errors={errors}
          error={error}
          onRefetch={() => refetch()}
          onExport={exportForm}
        />
        <ActionModal
          form={form}
          busy={mutation.isLoading}
          onClose={() => setForm(null)}
        />
        {exportId && (
          <ExportStatus id={exportId} onError={(text) => setErrors((prev) => [...prev, text])} />
        )}

        {tab === 'overview' && (
          <OverviewTab
            workspace={d}
            projectId={projectId}
            versionId={versionId}
            timezone={timezone}
            permission={permission}
            open={open}
            execute={execute}
            proposal={proposal}
            setProposal={setProposal}
          />
        )}

        {tab === 'sources' && (
          <SourcesTab
            workspace={d}
            projectId={projectId}
            versionId={versionId}
            timezone={timezone}
            editable={editable}
            permission={permission}
            open={open}
            execute={execute}
            fail={fail}
            setMessage={setMessage}
            setErrors={setErrors}
            token={token}
            file={file}
            setFile={setFile}
            importKind={importKind}
            setImportKind={setImportKind}
            dateFormat={dateFormat}
            setDateFormat={setDateFormat}
            separator={separator}
            setSeparator={setSeparator}
            upload={upload}
            uploading={uploading}
            uploadOriginal={uploadOriginal}
            uploadingOriginal={uploadingOriginal}
            detailedImportId={detailedImportId}
            setDetailedImportId={setDetailedImportId}
            loadingImport={loadingImport}
            importError={importError}
            workOptions={workOptions}
            calendarOptions={calendarOptions}
            fieldSites={fieldSites}
            fieldSiteOptions={fieldSiteOptions}
            fieldSitesError={!!fieldSitesQ.error}
            mtoRunOptions={mtoRunOptions}
            mtoRowOptions={mtoRowOptions}
            latestMtoRunId={latestMtoRunId}
          />
        )}

        {tab === 'schedule' && (
          <ScheduleTab
            workspace={d}
            projectId={projectId}
            versionId={versionId}
            version={version}
            timezone={timezone}
            editable={editable}
            permission={permission}
            open={open}
            execute={execute}
            setMessage={setMessage}
            activityForm={activityForm}
            workOptions={workOptions}
            calendarOptions={calendarOptions}
            wbsOptions={wbsOptions}
            fileOptions={fileOptions}
            timestamps={timestamps}
            params={params}
            setParams={setParams}
          />
        )}

        {tab === 'periods' && (
          <PeriodsTab
            workspace={d}
            projectId={projectId}
            versionId={versionId}
            permission={permission}
            open={open}
            execute={execute}
            workOptions={workOptions}
            dailyItems={dailyItems}
            setDailyItems={setDailyItems}
            periodStart={periodStart}
            setPeriodStart={setPeriodStart}
            periodEnd={periodEnd}
            setPeriodEnd={setPeriodEnd}
          />
        )}

        {tab === 'actual' && (
          <ActualTab
            workspace={d}
            projectId={projectId}
            versionId={versionId}
            timezone={timezone}
            permission={permission}
            open={open}
            execute={execute}
            workOptions={workOptions}
            fileOptions={fileOptions}
          />
        )}

        {tab === 'planfact' && (
          <PlanFactTab
            workspace={d}
            permission={permission}
            createDocument={createDocument}
          />
        )}

        {tab === 'milestones' && (
          <MilestonesTab
            workspace={d}
            projectId={projectId}
            timezone={timezone}
            canWrite={can_write}
            permission={permission}
            open={open}
            execute={execute}
            fail={fail}
            setMessage={setMessage}
            fileOptions={fileOptions}
            letterOptions={letterOptions}
            evidenceOptions={evidenceOptions}
            registerDispatchEvidence={registerDispatchEvidence}
            createFunctionRequest={createFunctionRequest}
            creatingReq={creatingReq}
            preparedMessages={preparedMessages}
            setPreparedMessages={setPreparedMessages}
          />
        )}

        {tab === 'recovery' && (
          <RecoveryTab
            workspace={d}
            permission={permission}
            open={open}
            execute={execute}
            createDocument={createDocument}
            workOptions={workOptions}
            fileOptions={fileOptions}
          />
        )}

        {tab === 'rd' && (
          <Card>
            <RdDatesTab
              projectId={projectId}
              version={d.version}
              canEdit={permission() && !d.history_read_only}
              execute={execute}
              open={open}
              onError={fail}
              workOptions={workOptions}
            />
          </Card>
        )}

        {tab === 'documents' && (
          <DocumentsTab
            workspace={d}
            timezone={timezone}
            token={token}
            permission={permission}
            exportForm={exportForm}
            createDocument={createDocument}
            setErrors={setErrors}
          />
        )}

        {tab === 'history' && (
          <HistoryTab
            workspace={d}
            projectId={projectId}
            timezone={timezone}
            auditDetails={auditDetails}
            auditPending={auditPending}
            fetchingAuditPage={fetchingAuditPage}
            loadAuditDetails={loadAuditDetails}
          />
        )}
      </Stack>
    </CollectionContext.Provider>
  );
}
